import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiGlobe, FiBarChart2, FiCheckCircle } = FiIcons;

const QuestionStep = ({
  question,
  selectedAnswer,
  onAnswerChange,
  questionNumber,
  totalQuestions,
  category
}) => {
  const { text, options, values } = question;

  // Get category icon
  const getCategoryIcon = () => {
    switch(category) {
      case "Brand Positioning":
        return FiTarget;
      case "Website Performance":
        return FiGlobe;
      case "Marketing System":
        return FiBarChart2;
      default:
        return FiTarget;
    }
  };

  // Get category color
  const getCategoryColor = () => {
    switch(category) {
      case "Brand Positioning":
        return "text-blue-500 bg-blue-500";
      case "Website Performance":
        return "text-green-500 bg-green-500";
      case "Marketing System":
        return "text-purple-500 bg-purple-500";
      default:
        return "text-primary-500 bg-primary-500";
    }
  };

  // Calculate category-specific question number
  const categoryQuestions = {
    "Brand Positioning": [1, 2, 3],
    "Website Performance": [4, 5, 6, 7],
    "Marketing System": [8, 9, 10]
  };

  let categoryQuestionNum = 1;
  let categoryTotal = 3;

  if (category === "Brand Positioning") {
    categoryQuestionNum = questionNumber;
    categoryTotal = 3;
  } else if (category === "Website Performance") {
    categoryQuestionNum = questionNumber - 3;
    categoryTotal = 4;
  } else if (category === "Marketing System") {
    categoryQuestionNum = questionNumber - 7;
    categoryTotal = 3;
  }

  const IconComponent = getCategoryIcon();
  const categoryColors = getCategoryColor();

  const handleOptionClick = (value) => {
    onAnswerChange(value);
    // Auto-advance after a short delay to show selection
    setTimeout(() => {
      // This will be handled by the parent component
    }, 300);
  };

  return (
    <div className="space-y-8 max-w-3xl mx-auto">
      {/* Category Badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-2"
      >
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 ${categoryColors.replace('bg-', 'border-')} bg-opacity-20`}>
          <SafeIcon icon={IconComponent} className={`w-5 h-5 ${categoryColors.split(' ')[0]}`} />
          <span className={`font-bold ${categoryColors.split(' ')[0]}`}>{category}</span>
        </div>
        <div className="text-sm text-gray-400">
          Question {categoryQuestionNum} of {categoryTotal} in this category
        </div>
      </motion.div>

      {/* Mini Progress Bar for Category */}
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md mx-auto"
      >
        <div className="h-2 bg-dark-400 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(categoryQuestionNum / categoryTotal) * 100}%` }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`h-full ${categoryColors.split(' ')[1]} rounded-full`}
          />
        </div>
      </motion.div>

      {/* Question Header */}
      <div className="text-center space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-3"
        >
          <div className="text-sm font-medium text-primary-500 uppercase tracking-wide">
            Overall Progress: Question {questionNumber} of {totalQuestions}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight px-4">
            {text}
          </h2>
        </motion.div>
      </div>

      {/* Options */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-4"
      >
        {options.map((option, optionIndex) => {
          const isSelected = selectedAnswer === values[optionIndex];

          return (
            <motion.button
              key={optionIndex}
              onClick={() => handleOptionClick(values[optionIndex])}
              className={`w-full p-5 md:p-6 rounded-xl border-2 text-left transition-all duration-300 relative overflow-hidden ${
                isSelected
                  ? 'border-primary-600 bg-primary-600 bg-opacity-20 shadow-lg ring-2 ring-primary-600 ring-opacity-50'
                  : 'border-dark-400 bg-dark-500 hover:border-primary-500 hover:bg-dark-400 hover:shadow-md'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + optionIndex * 0.1 }}
            >
              {/* Selection ripple effect */}
              {isSelected && (
                <motion.div
                  className="absolute inset-0 bg-primary-600 opacity-10"
                  initial={{ scale: 0, opacity: 0.3 }}
                  animate={{ scale: 2, opacity: 0 }}
                  transition={{ duration: 0.6 }}
                />
              )}

              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center space-x-4 flex-1">
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex-shrink-0 transition-all duration-200 flex items-center justify-center ${
                      isSelected
                        ? 'border-primary-600 bg-primary-600'
                        : 'border-gray-500'
                    }`}
                  >
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                      >
                        <SafeIcon icon={FiCheckCircle} className="w-full h-full text-white" />
                      </motion.div>
                    )}
                  </div>
                  <span className={`text-base md:text-lg font-medium ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                    {option}
                  </span>
                </div>

                {/* Checkmark badge on selected */}
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0, x: 20 }}
                    animate={{ scale: 1, x: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
                    className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-bold"
                  >
                    SELECTED
                  </motion.div>
                )}
              </div>
            </motion.button>
          );
        })}
      </motion.div>
    </div>
  );
};

export default QuestionStep;